import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

import {
  MdClose,
  MdSend,
  MdDescription,
  MdPerson,
  MdPhone,
  MdEmail,
  MdBusiness,
  MdAttachFile,
  MdDeleteOutline,
  MdCheckCircleOutline,
  MdErrorOutline,
  MdOutlinePersonSearch,
} from "react-icons/md";

export default function CreateQuery({
  closeModal,
  refresh,
}) {
  const API = process.env.REACT_APP_API_URL;

  const userId = localStorage.getItem("user_id");
  const userBranchId = localStorage.getItem("branch_id");
  const userRole = localStorage.getItem("userrole");
  const isAdmin = userRole === "admin";

  // =========================================================
  // STATE
  // =========================================================

  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [queryHeading, setQueryHeading] = useState("");
  const [queryDescription, setQueryDescription] = useState("");
  const [buildingBlock, setBuildingBlock] = useState("");

  const [department, setDepartment] = useState("");
  const [priority, setPriority] = useState("Medium");

  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(userBranchId || "");
  const [queryHeadings, setQueryHeadings] = useState([]);
  const [loadingHeadings, setLoadingHeadings] = useState(true);

  const [selectedFile, setSelectedFile] = useState(null);

  const [requestedDepartmentUser, setRequestedDepartmentUser] = useState("");
  const [requestedDepartment, setRequestedDepartment] = useState("");

  const [loadingDepartments, setLoadingDepartments] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  // =========================================================
  // FETCH DEPARTMENTS
  // =========================================================

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);

        const res = await axiosInstance.get(
          `${API}/api/get/dep/`
        );

        setDepartments(res.data || []);
      } catch (err) {
        console.log(
          "Error fetching departments:",
          err
        );

        setError(
          "Unable to load departments."
        );
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [API]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (isAdmin) return;

      try {
        const res = await axiosInstance.get(`${API}/api/me/`);
        if (res.data?.branch_id) {
          setBranch(String(res.data.branch_id));
          localStorage.setItem("branch_id", res.data.branch_id);
          localStorage.setItem("branch_name", res.data.branch_name || "");
        }
      } catch (err) {
        setError("Unable to load your assigned branch.");
      }
    };

    fetchCurrentUser();
  }, [API, isAdmin]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axiosInstance.get(`${API}/api/get/branches/`);
        setBranches(res.data || []);
      } catch (err) {
        setError("Unable to load branches.");
      }
    };

    fetchBranches();
  }, [API]);

  useEffect(() => {
    const fetchQueryHeadings = async () => {
      try {
        setLoadingHeadings(true);

        const res = await axiosInstance.get(
          `${API}/api/query-headings/`
        );

        setQueryHeadings(res.data || []);
      } catch (err) {
        console.log("Error fetching query headings:", err);
        setError("Unable to load query headings.");
      } finally {
        setLoadingHeadings(false);
      }
    };

    fetchQueryHeadings();
  }, [API]);

  // =========================================================
  // FILE SELECT
  // =========================================================

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Optional size validation - 5 MB
    if (file.size > 5 * 1024 * 1024) {
      setError(
        "File size must be less than 5 MB."
      );

      e.target.value = "";
      setSelectedFile(null);

      return;
    }

    setError("");
    setSelectedFile(file);
  };

  // =========================================================
  // REMOVE FILE
  // =========================================================

  const removeFile = () => {
    setSelectedFile(null);

    const fileInput =
      document.getElementById(
        "query-attachment"
      );

    if (fileInput) {
      fileInput.value = "";
    }
  };

  // =========================================================
  // VALIDATION
  // =========================================================

  const validateForm = () => {
    if (!branch) {
      setError("Please select a branch.");
      return false;
    }

    // if (!email.trim()) {
    //   setError("Email is required.");
    //   return false;
    // }

    // if (!mobile.trim()) {
    //   setError("Mobile number is required.");
    //   return false;
    // }

    if (!requestedDepartment.trim()) {
      setError("Requested department is required.");
      return false;
    }

    if (!queryHeading.trim()) {
      setError("Query heading is required.");
      return false;
    }

    if (!queryDescription.trim()) {
      setError(
        "Query description is required."
      );
      return false;
    }

    if (!department) {
      setError(
        "Please select a department."
      );
      return false;
    }

    return true;
  };

  // =========================================================
  // SUBMIT QUERY
  // =========================================================

  const submitQuery = async () => {
    setError("");

    if (!validateForm()) {
        return;
    }

    try {
        setSubmitting(true);

        const formData = new FormData();

        formData.append(
            "user_id",
            userId || ""
        );

        formData.append(
          "branch_id",
          branch
        );

        formData.append(
            "email",
            email.trim()
        );

        formData.append(
            "mobile",
            mobile.trim()
        );

        formData.append(
            "building_block",
            buildingBlock.trim()
        );

        formData.append(
            "query_heading",
            queryHeading.trim()
        );

        formData.append(
            "query_description",
            queryDescription.trim()
        );

        formData.append(
            "department",
            department
        );

        formData.append(
          "priority",
          priority
        );

        formData.append(
            "requested_department",
            requestedDepartment
        );

        formData.append(
            "requested_department_user",
            requestedDepartmentUser.trim()
        );

        if (selectedFile) {
            formData.append(
                "image",
                selectedFile
            );
        }

        const response =
            await axiosInstance.post(
                "/api/query/create/",
                formData
            );

        console.log(
            "Query created:",
            response.data
        );

        await refresh();

        closeModal();

    } catch (err) {

        console.error(
            "Query submission error:",
            err
        );

        const backendMessage =
            err.response?.data?.message ||
            err.response?.data?.detail ||
            err.response?.data?.error;

        setError(
            backendMessage ||
            "Unable to submit query. Please try again."
        );

    } finally {
        setSubmitting(false);
    }
};

  // =========================================================
  // ENTER / CTRL+ENTER
  // =========================================================

  const handleKeyDown = (e) => {
    if (
      e.ctrlKey &&
      e.key === "Enter" &&
      !submitting
    ) {
      submitQuery();
    }
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5"
      onClick={closeModal}
    >

      {/* =====================================================
          MODAL
      ===================================================== */}

      <div
        className="relative w-full max-w-2xl max-h-[94vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* ===================================================
            HEADER
        =================================================== */}

        <div
          className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200 bg-white flex-shrink-0"
        >

          <div className="flex items-center gap-3">

            <div
              className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"
            >
              <MdDescription size={23} />
            </div>

            <div>

              <h2 className="text-lg font-bold text-slate-800">
                Create Query
              </h2>

              <p className="text-xs text-slate-500 mt-0.5">
                Submit a new support request
              </p>

            </div>

          </div>

          {/* CLOSE */}

          <button
            type="button"
            onClick={closeModal}
            disabled={submitting}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 transition"
          >
            <MdClose size={22} />
          </button>

        </div>

        {/* ===================================================
            FORM CONTENT
        =================================================== */}

        <div className="overflow-y-auto">

          <div className="p-5 sm:p-6 space-y-6">

            {/* =================================================
                ERROR MESSAGE
            ================================================= */}

            {error && (

              <div
                className="flex items-start gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-red-700"
              >

                <MdErrorOutline
                  size={20}
                  className="mt-0.5 flex-shrink-0"
                />

                <div className="flex-1">

                  <p className="text-sm font-medium">
                    {error}
                  </p>

                </div>

                <button
                  type="button"
                  onClick={() =>
                    setError("")
                  }
                  className="text-red-400 hover:text-red-600"
                >
                  <MdClose size={17} />
                </button>

              </div>

            )}

            {/* =================================================
                CONTACT INFORMATION
            ================================================= */}

            <div>

              <div className="flex items-center gap-2 mb-3">

                <div
                  className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"
                >
                  <MdPerson size={18} />
                </div>

                <div>

                  <h3 className="text-sm font-semibold text-slate-800">
                    User Information
                  </h3>

                  {/* <p className="text-xs text-slate-400">
                    Provide your contact details
                  </p> */}

                </div>

              </div>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >

                {/* EMAIL */}

                {/* <div>

                  <label
                    htmlFor="query-email"
                    className="block text-xs font-semibold text-slate-600 mb-1.5"
                  >
                    Email
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <div className="relative">

                    <MdEmail
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      id="query-email"
                      type="email"
                      value={email}
                      placeholder="Enter email"
                      onChange={(e) => {
                        setEmail(
                          e.target.value
                        );
                        setError("");
                      }}
                      className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                    />

                  </div>

                </div> */}

                {/* MOBILE */}

                {/* <div>

                  <label
                    htmlFor="query-mobile"
                    className="block text-xs font-semibold text-slate-600 mb-1.5"
                  >
                    Mobile
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <div className="relative">

                    <MdPhone
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      id="query-mobile"
                      type="tel"
                      value={mobile}
                      placeholder="Enter mobile number"
                      onChange={(e) => {
                        setMobile(
                          e.target.value
                        );
                        setError("");
                      }}
                      className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                    />

                  </div>

                </div> */}

              </div>

            </div>

            {/* =================================================
                REQUESTED USER
            ================================================= */}

            {isAdmin && (
              <div>
                <label
                  htmlFor="query-branch"
                  className="block text-xs font-semibold text-slate-600 mb-1.5"
                >
                  Branch
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <select
                  id="query-branch"
                  value={branch}
                  onChange={(e) => {
                    setBranch(e.target.value);
                    setError("");
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                >
                  <option value="">Select branch</option>
                  {branches.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div>

                <label
                  htmlFor="requested-user"
                  className="block text-xs font-semibold text-slate-600 mb-1.5"
                >
                  Requested User
                </label>

                <div className="relative">

                  <MdOutlinePersonSearch
                    size={19}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="requested-user"
                    type="text"
                    value={
                      requestedDepartmentUser
                    }
                    placeholder="Enter requested user name"
                    onChange={(e) => {
                      setRequestedDepartmentUser(
                        e.target.value
                      );
                      setError("");
                    }}
                    className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                  />

                </div>
              </div>


              <div>
                <label
                  htmlFor="requested-department"
                  className="block text-xs font-semibold text-slate-600 mb-1.5"
                >
                  Requested Department
                  <span className="text-red-500 ml-1">
                      *
                    </span>
                </label>

                <div className="relative">
                  <MdBusiness
                    size={19}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  

                  <input
                    id="requested-department"
                    type="text"
                    value={requestedDepartment}
                    onChange={(e) => {
                      setRequestedDepartment(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter requested department"
                    className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                  />
                  
                </div>
              </div>
            </div>

            <div>
    <label
        htmlFor="building-block"
        className="block text-xs font-semibold text-slate-600 mb-1.5"
    >
        Building Block
        <span className="text-red-500 ml-1">*</span>
    </label>

    <input
        id="building-block"
        type="text"
        value={buildingBlock}
        placeholder="Enter building / block"
        onChange={(e) => {
            setBuildingBlock(e.target.value);
            setError("");
        }}
        className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
    />
</div>

            {/* =================================================
                QUERY DETAILS
            ================================================= */}

            <div>

              <div className="flex items-center gap-2 mb-3">

                <div
                  className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"
                >
                  <MdDescription size={18} />
                </div>

                <div>

                  <h3 className="text-sm font-semibold text-slate-800">
                    Query Details
                  </h3>

                  {/* <p className="text-xs text-slate-400">
                    Describe the issue you need help with
                  </p> */}

                </div>

              </div>

              <div className="space-y-4">

                {/* HEADING */}

                <div>

                  <label
                    htmlFor="query-heading"
                    className="block text-xs font-semibold text-slate-600 mb-1.5"
                  >
                    Query Heading
                    <span className="text-red-500 ml-1">
                      *
                    </span>
                  </label>

                  <select
                    id="query-heading"
                    value={queryHeading}
                    onChange={(e) => {
                      setQueryHeading(
                        e.target.value
                      );
                      setError("");
                    }}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none appearance-none cursor-pointer focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    disabled={loadingHeadings}
                  >
                    <option value="">
                      {loadingHeadings
                        ? "Loading query headings..."
                        : queryHeadings.length
                          ? "Select query heading"
                          : "No query headings available"}
                    </option>

                    {queryHeadings.map((heading) => (
                      <option
                        key={heading.id}
                        value={heading.name}
                      >
                        {heading.name}
                      </option>
                    ))}
                  </select>

                </div>

                {/* DESCRIPTION */}

                <div>

                  <div className="flex items-center justify-between mb-1.5">

                    <label
                      htmlFor="query-description"
                      className="text-xs font-semibold text-slate-600"
                    >
                      Description
                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <span className="text-[11px] text-slate-400">
                      {queryDescription.length} characters
                    </span>

                  </div>

                  <textarea
                    id="query-description"
                    rows={5}
                    value={queryDescription}
                    placeholder="Describe your issue in detail..."
                    onChange={(e) => {
                      setQueryDescription(
                        e.target.value
                      );
                      setError("");
                    }}
                    onKeyDown={handleKeyDown}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 outline-none resize-none focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                  />

                  {/* <p className="mt-1 text-[11px] text-slate-400">
                    Tip: Press Ctrl + Enter to submit.
                  </p> */}

                </div>

              </div>

            </div>


            {/* =================================================
                DEPARTMENT
            ================================================= */}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div>
                <label
                  htmlFor="query-department"
                  className="block text-xs font-semibold text-slate-600 mb-1.5"
                >
                  Assign Department
                  <span className="text-red-500 ml-1">
                    *
                  </span>
                </label>

                <div className="relative">

                  <MdBusiness
                    size={19}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />

                  <select
                    id="query-department" value={department} onChange={(e) => {
                      setDepartment(e.target.value);
                      setError("");
                    }}
                    disabled={loadingDepartments}
                    className="w-full h-10 pl-10 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none appearance-none cursor-pointer focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-60 disabled:cursor-not-allowed transition"
                  >

                    <option value="">
                      {loadingDepartments
                        ? "Loading departments..."
                        : "Select department"}
                    </option>

                    {departments.map((dept) => (
                        <option
                          key={dept.id}
                          value={dept.name}
                        >
                          {dept.name}
                        </option>
                    ))}

                  </select>

                </div>
              </div>

              <div>
                <label
                  htmlFor="query-priority"
                  className="block text-xs font-semibold text-slate-600 mb-1.5"
                >
                  Priority
                </label>

                <select
                  id="query-priority"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-700 outline-none appearance-none cursor-pointer focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 transition"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>

            </div>

            {/* =================================================
                ATTACHMENT
            ================================================= */}

            <div>

              <label
                className="block text-xs font-semibold text-slate-600 mb-1.5"
              >
                Attachment
                <span className="ml-1 text-[11px] font-normal text-slate-400">
                  Optional
                </span>
              </label>

              {!selectedFile ? (

                <label
                  htmlFor="query-attachment"
                  className="flex flex-col items-center justify-center w-full min-h-[110px] rounded-xl border-2 border-dashed border-slate-200 hover:bg-sky-50 hover:border-sky-300 cursor-pointer transition"
                >

                  <div
                    className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-slate-500 flex items-center justify-center mb-2"
                  >
                    <MdAttachFile
                      size={20}
                    />
                  </div>

                  <p className="text-sm font-medium text-slate-600">
                    Click to upload attachment
                  </p>

                  <p className="text-[11px] text-slate-400 mt-1">
                    Maximum file size: 5 MB
                  </p>

                  <input
                    id="query-attachment"
                    type="file"
                    className="hidden"
                    onChange={
                      handleFileChange
                    }
                  />

                </label>

              ) : (

                <div
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200"
                >

                  <div className="flex items-center gap-3 min-w-0">

                    <div
                      className="w-9 h-9 rounded-lg bg-white border border-slate-200 text-sky-600 flex items-center justify-center flex-shrink-0"
                    >
                      <MdAttachFile
                        size={19}
                      />
                    </div>

                    <div className="min-w-0">

                      <p className="text-sm font-medium text-slate-700 truncate">
                        {selectedFile.name}
                      </p>

                      <p className="text-[11px] text-slate-400">
                        {(
                          selectedFile.size /
                          1024
                        ).toFixed(1)}{" "}
                        KB
                      </p>

                    </div>

                  </div>

                  <button
                    type="button"
                    onClick={removeFile}
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition flex-shrink-0"
                  >
                    <MdDeleteOutline
                      size={19}
                    />
                  </button>

                </div>

              )}

            </div>

          </div>

        </div>

        {/* ===================================================
            FOOTER
        =================================================== */}

        <div
          className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-t border-slate-200 flex-shrink-0"
        >

          <p className="text-xs text-slate-400">
            Fields marked with
            <span className="text-red-500 ml-1">
              *
            </span>{" "}
            are required.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-2"
          >

            {/* CANCEL */}

            <button
              type="button"
              onClick={closeModal}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50 transition"
            >
              <MdClose size={18} />
              Cancel
            </button>

            {/* CREATE */}

            <button
              type="button"
              onClick={submitQuery}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-sm transition"
            >

              {submitting ? (

                <>
                  <span
                    className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                  />

                  Submitting...
                </>

              ) : (

                <>
                  <MdSend size={18} />
                  Create Query
                </>

              )}

            </button>

          </div>

        </div>

      </div>

    </div>
  );
}